import React from 'react';
import { Link } from 'react-router-dom';
import QuoteForm from './QuoteForm';
import Rotator from './Rotator';
import HeroVideo from './HeroVideo';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

// *word* -> <em>word</em>; newline -> <br/>
export const rich = (s) => {
  if (!s) return null;
  return s.split('\n').map((line, li, arr) => (
    <React.Fragment key={li}>
      {line.split(/\*([^*]+)\*/g).map((part, i) => (i % 2 ? <em key={i}>{part}</em> : part))}
      {li < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

const paras = (s, className) =>
  s ? s.split(/\n\s*\n/).map((p, i) => <p key={i} className={className}>{rich(p)}</p>) : null;

const imgUrl = (image, scale = 'great') => {
  const it = Array.isArray(image) ? image[0] : image;
  const id = it?.['@id'] || it?.url;
  if (!id) return null;
  return `${flattenToAppURL(id)}/@@images/image/${scale}`;
};

const Btn = ({ data, cls = 'gf-btn-dark' }) => {
  if (!data.button_label) return null;
  const href = data.button_href || '#';
  const c = `gf-btn ${data.button_style === 'outline' ? '' : cls}`.trim();
  return href.startsWith('#') || href.startsWith('http') ? (
    <a href={href} className={c}>{data.button_label}</a>
  ) : (
    <Link to={href} className={c}>{data.button_label}</Link>
  );
};

export const Figure = ({ item, scale, className = 'gf-img', responsive = false }) => {
  const src = imgUrl(item?.image, scale);
  // 0.3.9: about layout serves 800w/1000w Plone scales
  const srcSet = responsive && src ? `${imgUrl(item.image, 'large')} 800w, ${imgUrl(item.image, 'great')} 1000w` : undefined;
  return (
    <figure className="gf-figure">
      <div className={className}>
        {src ? <img src={src} srcSet={srcSet} sizes={srcSet ? '(max-width: 860px) 100vw, 45vw' : undefined} alt={item.alt || item.caption || ''} loading="lazy" /> : <div className="gf-img-empty">Choose an image</div>}
      </div>
      {item?.caption && <figcaption>{item.caption}</figcaption>}
    </figure>
  );
};

const Eyebrow = ({ t }) => (t ? <span className="gf-eyebrow">{t}</span> : null);

const SectionView = ({ data = {} }) => {
  const items = data.items || [];
  const L = data.layout || 'hero';
  const id = data.anchor || undefined;
  let inner;
  switch (L) {
    case 'hero':
      inner = (
        <div className="gf-wrap gf-hero">
          <div>
            <Eyebrow t={data.eyebrow} />
            <h1>{rich(data.title)}</h1>
            {paras(data.text)}
            <Btn data={data} />
          </div>
          {items[0] && (items[0].video ? <HeroVideo item={items[0]} /> : <Figure item={items[0]} scale="huge" />)}
        </div>
      );
      break;
    case 'statement':
      inner = (
        <div className="gf-wrap gf-statement">
          <Eyebrow t={data.eyebrow} />
          <p className="gf-q">{rich(data.title)}</p>
          {paras(data.text)}
        </div>
      );
      break;
    case 'about':
      inner = (
        <div className="gf-wrap gf-about">
          {items[0] && <Figure item={items[0]} responsive />}
          <div>
            <Eyebrow t={data.eyebrow} />
            {data.title && <h2>{rich(data.title)}</h2>}
            {paras(data.text)}
            {data.subtitle && <h3>{rich(data.subtitle)}</h3>}
          </div>
        </div>
      );
      break;
    case 'services':
      inner = (
        <div className="gf-wrap gf-services">
          <div className="gf-services-head">
            <Eyebrow t={data.eyebrow} />
            {data.title && <h2>{rich(data.title)}</h2>}
          </div>
          <div className="gf-services-grid">
            {items.map((it, i) => (
              <div key={it['@id'] || i} className="gf-service">
                <h3>{rich(it.heading)}</h3>
                {paras(it.text)}
              </div>
            ))}
          </div>
        </div>
      );
      break;
    case 'steps':
      inner = (
        <div className="gf-wrap gf-steps">
          {items.map((it, i) => (
            <div key={it['@id'] || i}>
              <span className="gf-n">{it.number || String(i + 1).padStart(2, '0')}</span>
              <h3>{rich(it.heading)}</h3>
              {paras(it.text)}
            </div>
          ))}
        </div>
      );
      break;
    case 'strip':
      inner = (
        <div className="gf-wrap gf-strip">
          {items.map((it, i) => <Figure key={it['@id'] || i} item={it} />)}
        </div>
      );
      break;
    case 'quality':
      inner = (
        <div className="gf-quality">
          <div className="gf-wrap">
            <Eyebrow t={data.eyebrow} />
            <blockquote>{rich(data.title)}</blockquote>
          </div>
        </div>
      );
      break;
    case 'edit':
      inner = (
        <div className="gf-edit">
          <div className="gf-wrap">
            <div className="gf-edit-head">
              <div>
                <Eyebrow t={data.eyebrow} />
                <h2>{rich(data.title)}</h2>
              </div>
              <div>
                {paras(data.text, 'gf-edit-lede')}
                <Btn data={data} cls="" />
              </div>
            </div>
            <div className="gf-edit-grid">
              {items.map((it, i) => <Figure key={it['@id'] || i} item={it} scale={i === 0 ? 'huge' : 'great'} />)}
            </div>
          </div>
        </div>
      );
      break;
    case 'intro':
      inner = (
        <div className="gf-wrap gf-intro">
          <Eyebrow t={data.eyebrow} />
          <h1>{rich(data.title)}</h1>
          {paras(data.text)}
        </div>
      );
      break;
    case 'portfolio':
      inner = (
        <div className={`gf-wrap gf-portfolio${data.feature ? ' gf-portfolio-feature' : ''}`}>
          {(data.eyebrow || data.title) && (
            <div className="gf-portfolio-head">
              <Eyebrow t={data.eyebrow} />
              {data.title && <h2>{rich(data.title)}</h2>}
              {paras(data.text)}
            </div>
          )}
          <div className="gf-portfolio-grid">
            {items.map((it, i) => (
              <article key={it['@id'] || i} className="gf-card">
                {it.link ? (
                  <Link to={it.link} className="gf-card-link" aria-label={`View the ${it.heading || ''} collection`.replace(/\*/g, '')}>
                    <Figure item={it} />
                    <span className="gf-card-more" aria-hidden="true">View collection <span className="gf-arrow">→</span></span>
                  </Link>
                ) : (
                  <Figure item={it} />
                )}
                <div className="gf-card-body">
                  {it.client && <span className="gf-eyebrow gf-card-client">{it.client}</span>}
                  {it.heading && <h3>{it.link ? <Link to={it.link}>{rich(it.heading)}</Link> : rich(it.heading)}</h3>}
                  {paras(it.text)}
                  {it.feeling && (
                    <p className="gf-card-feeling"><span>The feeling</span>{rich(it.feeling)}</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      );
      break;
    case 'rotator':
      inner = (
        <div className="gf-wrap gf-rotator">
          {(data.eyebrow || data.title) && (
            <div className="gf-portfolio-head">
              <Eyebrow t={data.eyebrow} />
              {data.title && <h2>{rich(data.title)}</h2>}
            </div>
          )}
          <Rotator items={items} />
        </div>
      );
      break;
    case 'prose':
      inner = (
        <div className="gf-wrap gf-prose">
          {data.eyebrow && <Eyebrow t={data.eyebrow} />}
          {data.title && <h2>{rich(data.title)}</h2>}
          {(data.text || '').split(/\n\s*\n/).map((p, i) =>
            p.startsWith('## ') ? <h3 key={i}>{rich(p.slice(3))}</h3> : <p key={i}>{rich(p)}</p>,
          )}
        </div>
      );
      break;
    case 'teaser':
      inner = (
        <div className="gf-wrap gf-teaser">
          <p className="gf-teaser-line">{rich(data.title)}</p>
          {data.button_label && (
            <Link to={data.button_href || '/process'} className="gf-teaser-link">{data.button_label} <span aria-hidden="true">→</span></Link>
          )}
        </div>
      );
      break;
    case 'form':
      inner = <QuoteForm data={data} />;
      break;
    case 'cta':
    default:
      inner = (
        <div className="gf-wrap gf-cta">
          <Eyebrow t={data.eyebrow} />
          <h2>{rich(data.title)}</h2>
          {paras(data.text)}
          <Btn data={data} />
        </div>
      );
  }
  return (
    <section id={id} className={`gf-section gf-section-${L}`}>
      {inner}
    </section>
  );
};

export default SectionView;
